import { z } from 'zod';
import { pluralize } from './string';

// Numeric epochs below this are treated as seconds (legacy bop-api mixes s and ms)
const EPOCH_MS_THRESHOLD = 1e12;

/** Epoch seconds/ms (number or all-digit string), parseable date string, or Date → Date. */
export const toDate = (value: number | string | Date): Date => {
  if (value instanceof Date) return value;
  if (typeof value === 'number' || /^\d+$/.test(value)) {
    const epoch = Number(value);
    return new Date(epoch < EPOCH_MS_THRESHOLD ? epoch * 1000 : epoch);
  }
  return new Date(value);
};

/** Epoch ms from a seconds/ms epoch or parseable date string; NaN on unparseable input. */
export const toEpochMs = (value: number | string): number => toDate(value).getTime();

const ISO_DATE_TIME = z.iso.datetime({ offset: true });

/** Whether `value` is an ISO date and time with a `Z` or an offset, as a zod `z.iso.datetime()` field holds */
export const isIsoDateTime = (value: unknown): value is string => ISO_DATE_TIME.safeParse(value).success;

// LOCAL date for <input type="date"> values — the legacy toISOString() variant
// shifted late-evening users to yesterday (UTC), fixed deliberately here.
export const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

// LOCAL-time day bounds serialized with the operator's offset — legacy behavior
// kept deliberately: ops users expect "their" day
export const localStartOfDayIso = (dateInput: string): string => new Date(`${dateInput}T00:00:00`).toISOString();
export const localEndOfDayIso = (dateInput: string): string => new Date(`${dateInput}T23:59:59.999`).toISOString();

// UTC day-rounding moved from the Nuxt BFF (funds/transactions.post.ts): the picked
// calendar date becomes 00:00:00.000Z / 23:59:59.999Z of that same calendar day.
export const utcStartOfDayIso = (dateInput: string): string => new Date(`${dateInput}T00:00:00.000Z`).toISOString();
export const utcEndOfDayIso = (dateInput: string): string => new Date(`${dateInput}T23:59:59.999Z`).toISOString();

const END_OF_DAY = { hour: 23, minute: 59, second: 59, ms: 999 } as const;

// Local start/end-of-day as Dates: the picked calendar date becomes
// local midnight / 23:59:59.999, so the ISO strings sent upstream embed the operator's
// TZ offset — deliberate legacy behaviour, kept.
export const toLocalStartOfDay = (dateInput: string): Date => {
  const [year = 0, month = 1, day = 1] = dateInput.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const toLocalEndOfDay = (dateInput: string): Date => {
  const [year = 0, month = 1, day = 1] = dateInput.split('-').map(Number);
  return new Date(year, month - 1, day, END_OF_DAY.hour, END_OF_DAY.minute, END_OF_DAY.second, END_OF_DAY.ms);
};

/**
 * `YYYY-MM-DD` (native date input) → UTC-anchored Date, so UTC day rounding
 * lands on the exact day the operator picked in any timezone — the legacy local-noon
 * normalisation trick replaced by direct UTC construction.
 */
export const parseDateInputToUtc = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const toUtcDateKey = (value: number | Date) => new Date(value).toISOString().slice(0, 10);

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_UTC = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.\d+)?Z$/;

/**
 * Epoch ms of a `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM:SS[.fff…]Z` string, or null for anything else,
 * including impossible dates that `Date` would roll over (Feb 30 becoming Mar 2).
 */
export const parseStrictUtcDate = (value: string): number | null => {
  const text = DATE_ONLY.test(value) ? `${value}T00:00:00Z` : value;
  const match = ISO_UTC.exec(text);
  if (!match) return null;
  const date = new Date(`${match[1]}${(match[2] ?? '').slice(0, 4)}Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 19) === match[1] ? date.getTime() : null;
};

/** Epoch ms of midnight UTC on a `YYYY-MM-DD` day, NaN for anything else. */
export const utcDayMs = (day: string) => Date.parse(`${day}T00:00:00Z`);

export const utcDaysBetween = (from: string, to: string) => Math.round((utcDayMs(to) - utcDayMs(from)) / MS_PER_DAY);

const MS_PER_MINUTE = 60 * 1000;
const UTC_TIME = /^(\d{2}):(\d{2})$/;

/** Milliseconds after UTC midnight for an `HH:MM` time, or null when it is not one. Mirrors bop-api's downtime parser. */
export const parseUtcTime = (value: string): number | null => {
  const match = UTC_TIME.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours <= 23 && minutes <= 59 ? (hours * 60 + minutes) * MS_PER_MINUTE : null;
};

/** ISO instant for a `YYYY-MM-DD` day and an `HH:MM` time, both UTC. */
export const toUtcInstantIso = (day: string, time: string) => `${day}T${time}:00.000Z`;

export const describeDaysFromToday = (days: number) => {
  if (days < 0) return `${pluralize(-days, 'day')} ago`;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${pluralize(days, 'day')}`;
};
